import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Button, Drawer, Tooltip } from 'antd';
import { useAtomValue } from 'jotai';
import { DownloadOutlined } from '@ant-design/icons';
import { ExportTitle } from './components/ExportTitle';
import { ExportFooter } from './components/ExportFooter';
import { ExportCheckbox } from './components/ExportCheckbox';
import { exportDataAtom } from '@src/components/export/atom';
import { track } from '@src/utils';
import { CLICK_EXPORT_TRENDS } from '@src/utils/sensor/track';
import { hiddenOptions, optionNameMapping, sectionDisplayOrder } from './constants';
import { get, isEmpty } from 'lodash';
import { ExportCheckboxGroup } from './components/ExportCheckboxGroup';
import {
  getInvertedPercentageMetricByKey,
  getMetricMetaByKey,
} from '@src/utils/metrics/metricHelpers';
import { useSelector } from 'react-redux';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

const ExportOption = () => {
  const [open, setOpen] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const dimensionMeta = useSelector(dimensionsListSelectorKeyValue);
  const initSections = useRef<string[]>([]);
  const dimensionOptions = useRef<{ value: string; label: string }[]>([]);
  const metricOptions = useRef<{ value: string; label: string }[]>([]);

  const exportData: any = useAtomValue(exportDataAtom);

  const handleSectionChange = useCallback(
    (section: string) => {
      let newSections = [...sections];
      if (sections.includes(section)) {
        newSections = newSections.filter((i) => i !== section);
      } else {
        newSections.push(section);
      }
      setSections(newSections);
    },
    [sections],
  );

  const handleCheckboxGroupChange = useCallback(
    (key: string, selected: string[]) => {
      let setFunc;
      switch (key) {
        case 'metrics-panel':
          setFunc = setSelectedMetrics;
          break;
        case 'dimensions-panel':
          setFunc = setSelectedDimensions;
          break;
        default:
          break;
      }
      setFunc?.(selected);
      if (
        (!sections.includes(key) && selected.length === 0) ||
        (sections.includes(key) && selected.length > 0)
      ) {
        // sections won't change, so return directly
        return;
      }
      handleSectionChange(key);
    },
    [handleSectionChange, sections],
  );

  useEffect(() => {
    const res: string[] = [];
    dimensionOptions.current = [];
    metricOptions.current = [];
    initSections.current = [];
    const sectionsObj: Record<string, Record<string, any>> = exportData?.sections;
    if (!sectionsObj) {
      setSections(res);
      return;
    }
    sectionDisplayOrder.forEach((key) => {
      const value = sectionsObj[key] ?? {};
      if (key === 'dimensions-panel') {
        const dimensions = Object.keys(value)
          .filter((key) => key.startsWith('dimension-table::'))
          .map((key) => key.split('::')[1]);
        if (dimensions.length > 0) {
          dimensionOptions.current = dimensions.map((dimension) => {
            const dimensionName = (dimensionMeta as Record<string, string>)[dimension];
            return {
              value: dimension,
              label: dimensionName ?? dimension,
            };
          });
          res.push('dimensions-panel');
        }
        Object.keys(value)
          .filter((key) => !key.startsWith('dimension-table::'))
          .forEach((key) => {
            res.push(key);
          });
        return;
      }
      if (key === 'metrics-panel') {
        let headers: { key: string; title: string; type?: string }[] = get(
          value,
          'primary-timeseries.headers',
          [],
        );
        headers = headers
          .filter((metric) => metric.type !== 'timestamp')
          .filter((metric) => !getInvertedPercentageMetricByKey({ metricKey: metric.key }));
        metricOptions.current = headers.map((metric) => {
          const metricInfo = getMetricMetaByKey({
            metricKey: metric.key,
            flowOverride: !!flowId,
            frustrationSignalOverride: !!frustrationSignalId,
          });
          return {
            value: metric.key,
            label: [metricInfo?.displayName ?? metric.title, metricInfo?.subscript]
              .filter(Boolean)
              .join(' '),
          };
        });
        if (metricOptions.current.length > 0) {
          res.push('metrics-panel');
        }
        return;
      }
      if (!isEmpty(value)) {
        res.push(key);
      }
    });
    const metrics = metricOptions.current.map((metric) => metric.value);
    const dimensions = dimensionOptions.current.map((dimension) => dimension.value);
    setSelectedMetrics(metrics);
    setSelectedDimensions(dimensions);
    initSections.current = res;
    setSections(res);
  }, [dimensionMeta, exportData?.sections, flowId, frustrationSignalId, open]);

  const showDrawer = () => {
    track({ event: CLICK_EXPORT_TRENDS });
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const content = useMemo(() => {
    return initSections.current
      .map((key) => {
        if (hiddenOptions.includes(key)) {
          return null;
        }
        if (key === 'metrics-panel') {
          return (
            <ExportCheckboxGroup
              key={key}
              title={optionNameMapping[key]}
              groupKey={key}
              options={metricOptions.current}
              selected={selectedMetrics}
              onSelectedChange={handleCheckboxGroupChange}
            />
          );
        }
        if (key === 'dimensions-panel' && dimensionOptions.current.length > 0) {
          return (
            <ExportCheckboxGroup
              key={key}
              title={optionNameMapping[key]}
              groupKey={key}
              options={dimensionOptions.current}
              selected={selectedDimensions}
              onSelectedChange={handleCheckboxGroupChange}
            />
          );
        }
        return (
          <ExportCheckbox
            title={optionNameMapping[key]}
            key={key}
            checked={sections.includes(key)}
            onChange={() => handleSectionChange(key)}
          />
        );
      })
      .filter((comp) => !!comp);
  }, [
    sections,
    selectedMetrics,
    handleCheckboxGroupChange,
    selectedDimensions,
    handleSectionChange,
  ]);

  return (
    <>
      <Tooltip title="Export">
        <Button onClick={showDrawer} icon={<DownloadOutlined />} />
      </Tooltip>
      <Drawer
        title={<ExportTitle />}
        onClose={onClose}
        open={open}
        width={800}
        closeIcon={false}
        footer={
          <ExportFooter
            onCancel={onClose}
            // only 'Base' in sections
            disableExport={sections.length === 1}
            sections={sections}
            selectedMetrics={selectedMetrics}
            selectedDimensions={selectedDimensions}
          />
        }
      >
        {content}
      </Drawer>
    </>
  );
};

export default ExportOption;
